import produce from "immer";
import React, { useState } from "react";

import AssessmentSettings from "./AssessmentSettings";
import OkhatiDialog from "../../../../components/Dialog/Dialog";
import { notificationAdd } from "../../../../actions/notification";
import { resourceCentreActions as actions } from "../../../../actions";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { useParsedProseMirror } from "../../../Lab/LabEntry/LabTestSubRow";
import { SortOrderType } from "../../../Assessment/AssessmentForm/OPDComponents";
import { deserializeNode, proseMirrorOptions } from "../../../../components/RichTextfield";
import { updateUserResourceCentre } from "../../../../actions/resourceCentre";
import { ResourceCentre } from "../../../../interfaces/ResourceCentreInterface";

const orderOPDComponentLabels = ({
  componentLabels,
  sortOrder
}: {
  componentLabels: string[];
  sortOrder: SortOrderType[];
}): SortOrderType[] => {
  const combined = Array.from(new Set([...componentLabels, "tabular", "skinAnalysis"]));
  return combined.sort((a, b) => {
    const indexA = sortOrder.indexOf(a as SortOrderType);
    const indexB = sortOrder.indexOf(b as SortOrderType);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  }) as SortOrderType[];
};

// eslint-disable-next-line import/prefer-default-export
export const OpdSettings = ({
  showConfirmDialog,
  setShowConfirmDialog
}: {
  showConfirmDialog: boolean;
  setShowConfirmDialog: React.Dispatch<React.SetStateAction<boolean>>;
}): React.ReactNode => {
  const dispatch = useAppDispatch();

  // states
  const [loading, setLoading] = useState(false);
  const resourceCentre = useAppSelector((state) => state.resources.resourceCentres[0]);

  const [rc, setRc] = useState(resourceCentre);

  React.useEffect(() => {
    if (resourceCentre) {
      setRc(resourceCentre);
    }
  }, [resourceCentre]);

  const [assessmentSettings, setAssessmentSettings] = useState(rc.settings.assessmentSettings);

  const [assessmentRemarksState, setAssessmentRemarksState] = useParsedProseMirror(
    rc.settings.printSettings.assessmentPrintSettings.assessmentRemarks || "",
    proseMirrorOptions
  );

  const [orderedComponentLabels, setOrderedComponentLabels] = useState(
    orderOPDComponentLabels({
      componentLabels: Object.keys(rc.settings.assessmentSettings.componentLabels || {}) || [],
      sortOrder: rc.settings.assessmentSettings.sortOrder || []
    })
  );

  // vitals settings
  const [vitalSettings, setVitalSettings] = useState(rc.settings.vitalSettings);
  const [vitalsOrder, setVitalsOrder] = useState<Array<string>>(
    rc.settings.assessmentSettings?.vitalsOrder
  );

  // questionnaire settings
  const [questionnaireSettings, setQuestionnaireSettings] = useState(
    rc.settings.questionnaireSettings || {}
  );
  const [questionnairesOrder, setQuestionnairesOrder] = useState<Array<string>>(
    rc.settings.assessmentSettings?.questionnairesOrder || []
  );

  return (
    <>
      <AssessmentSettings
        resourceCentre={rc}
        onChange={(value) => setRc(value)}
        assessmentRemarksState={assessmentRemarksState}
        setAssessmentRemarksState={setAssessmentRemarksState}
        orderedComponentLabels={orderedComponentLabels}
        setOrderedComponentLabels={setOrderedComponentLabels}
        assessmentSettings={assessmentSettings}
        setAssessmentSettings={setAssessmentSettings}
        vitalSettings={vitalSettings}
        setVitalSettings={setVitalSettings}
        vitalsOrder={vitalsOrder}
        setVitalsOrder={setVitalsOrder}
        questionnairesOrder={questionnairesOrder}
        setQuestionnairesOrder={setQuestionnairesOrder}
        questionnaireSettings={questionnaireSettings}
        setQuestionnaireSettings={setQuestionnaireSettings}
      />

      {showConfirmDialog && (
        <OkhatiDialog
          title="Change opd settings ?"
          description="Your changes will be permanently saved."
          readMode={false}
          isLoading={loading}
          cancel={() => {
            setShowConfirmDialog(false);
          }}
          next={async () => {
            // handle assessment remarks
            let newRc = produce(rc, (draft: ResourceCentre) => {
              draft.settings.printSettings.assessmentPrintSettings.assessmentRemarks =
                deserializeNode(assessmentRemarksState.doc);
            });

            // handle component order and labels
            const orderedComponentLabelsWithoutOpdDuration = produce(
              orderedComponentLabels,
              (componentsDraft: Array<string>) => {
                // remove opdDuration from sortOrder
                // as we dont want to include it in orders settings
                const opdDurationIndex = componentsDraft.indexOf("opdDuration");
                if (opdDurationIndex > -1) {
                  componentsDraft.splice(opdDurationIndex, 1);
                }
                return componentsDraft;
              }
            );

            newRc = produce(newRc, (draft: ResourceCentre) => {
              const uniqueSortOrder = Array.from(new Set(orderedComponentLabelsWithoutOpdDuration));
              draft.settings.assessmentSettings = {
                ...draft.settings.assessmentSettings,
                ...assessmentSettings,
                customHeaderImage: rc.settings.assessmentSettings.customHeaderImage,
                sortOrder: uniqueSortOrder as SortOrderType[]
              };
            });

            // handle vital settings
            newRc = produce(newRc, (draft: ResourceCentre) => {
              draft.settings = {
                ...draft.settings,
                vitalSettings,
                questionnaireSettings,
                assessmentSettings: {
                  ...draft.settings.assessmentSettings,
                  vitalsOrder,
                  questionnairesOrder
                }
              };
            });

            try {
              setLoading(true);
              await dispatch(actions.putResourceCentre(newRc));
              setRc(newRc);
              updateUserResourceCentre(newRc);
              setShowConfirmDialog(false);
              dispatch(
                notificationAdd({
                  id: new Date().getUTCMilliseconds(),
                  variant: "success",
                  message: "Assessment Labels updated successfully.",
                  autoTimeout: true
                })
              );
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (e: any) {
              dispatch(
                notificationAdd({
                  id: new Date().getUTCMilliseconds(),
                  variant: "error",
                  message: e?.message || e?.data?.message || "Something went wrong",
                  autoTimeout: true
                })
              );
            } finally {
              setLoading(false);
            }
          }}
        />
      )}
    </>
  );
};
